import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Info, Ban, Clock, Truck, MapPin, Coffee } from "lucide-react";
import RouteMapLeaflet from "@/components/RouteMapLeaflet";

const hrs = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;
const dayName = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

export function DayMap({ legs, height = 300 }) {
  const ref = useRef(null); const mapRef = useRef(null); const layerRef = useRef(null);
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { scrollWheelZoom: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "&copy; OpenStreetMap", maxZoom: 19 }).addTo(map);
    mapRef.current = map; layerRef.current = L.layerGroup().addTo(map); map.setView([53.8, -2.5], 6);
    setTimeout(() => map.invalidateSize(), 150);
    return () => { map.remove(); mapRef.current = null; };
  }, []);
  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current; if (!map || !layer) return;
    layer.clearLayers();
    const pts = (legs || []).filter((l) => l.lat && l.lng).map((l) => [l.lat, l.lng]);
    if (pts.length > 1) L.polyline(pts, { color: "#4F46E5", weight: 4, opacity: 0.8 }).addTo(layer);
    (legs || []).forEach((l, i) => {
      if (!l.lat || !l.lng) return;
      L.marker([l.lat, l.lng], { icon: L.divIcon({ className: "", html: `<div style="background:#4F46E5;color:#fff;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)">${i + 1}</div>`, iconSize: [22, 22], iconAnchor: [11, 11] }) })
        .addTo(layer).bindTooltip(`${i + 1}. ${l.title} · ${l.postcode || ""}`);
    });
    if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 11 });
    setTimeout(() => map.invalidateSize(), 120);
  }, [legs]);
  return <div ref={ref} style={{ height }} className="w-full rounded-xl border border-slate-200 overflow-hidden" data-testid="run-preview-map" />;
}

function Tot({ icon: Icon, label, val }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
      <Icon className="w-3.5 h-3.5 text-indigo-500 mx-auto" />
      <div className="text-[10px] uppercase text-slate-400 mt-0.5">{label}</div>
      <div className="text-sm font-bold text-slate-800">{val}</div>
    </div>
  );
}

export function DayTimetable({ day, index, linkStops = true }) {
  const dd = day; const tot = dd.totals; const cfg = dd.config || {};
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`run-preview-day-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-900">{dayName(cfg.date)}</h3>
        <span className="text-xs text-slate-400">{cfg.van_title || "Van"}{cfg.driver ? ` · ${cfg.driver}` : ""} · {cfg.start_time}–{cfg.end_time}</span>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-3">
        <Tot icon={Clock} label="Driving" val={hrs(tot.drive_mins)} />
        <Tot icon={Truck} label="On-site" val={hrs(tot.onsite_mins)} />
        <Tot icon={MapPin} label="Mileage" val={`${tot.miles} mi`} />
        <Tot icon={Coffee} label="Finish" val={tot.finish_time} />
      </div>
      {dd.legs.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No stops.</p> : (
        <div className="space-y-2">
          {dd.legs.map((l, idx) => (
            <div key={l.shift_id} className={`rounded-lg border p-2.5 ${l.late_for_fixed ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
              <div className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{idx + 1}</span>
                <div className="min-w-0 flex-1">
                  {linkStops && l.task_legacy_id ? (
                    <Link to={`/tasks/${l.task_legacy_id}`} className="text-sm font-semibold text-indigo-700 hover:underline truncate block" data-testid={`run-stop-link-${l.shift_id}`}>{l.title}</Link>
                  ) : (
                    <div className="text-sm font-semibold text-slate-800 truncate">{l.title}</div>
                  )}
                  <div className="text-[11px] text-slate-400">{l.postcode}{l.fixed_time ? ` · fixed ${l.fixed_time}` : ""}</div>
                  <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>drive {l.travel_mins}m ({l.travel_miles}mi, <em>{l.travel_source}</em>)</span>
                    <span>arr <b>{l.arrival}</b></span>
                    {l.break_mins > 0 && <span className="text-amber-600">+break {l.break_mins}m</span>}
                    <span>on-site {l.onsite_start}–{l.departure}</span>
                  </div>
                  {l.late_for_fixed && <div className="text-[11px] text-amber-700 mt-0.5">⚠ Arrives after the fixed {l.fixed_time} appointment</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RunPreview({ preview, showMaps = true, scan = null }) {
  const [activeDay, setActiveDay] = useState(0);
  if (!preview) return null;
  const t = preview.traffic || {};
  return (
    <div className="space-y-4" data-testid="run-preview">
      <div className={`rounded-xl border p-3 flex items-start gap-2.5 text-sm ${t.used_google ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="run-preview-traffic">
        <Info className="w-4 h-4 mt-0.5 shrink-0" /> <span>{t.note}</span>
      </div>
      {preview.unassigned && preview.unassigned.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="run-preview-unassigned">
          <h3 className="text-sm font-bold text-rose-800 flex items-center gap-1.5 mb-2"><Ban className="w-4 h-4" /> {preview.unassigned.length} shift(s) could not be fitted</h3>
          <ul className="space-y-1 text-sm text-rose-700">
            {preview.unassigned.map((u) => <li key={u.shift_id}><span className="font-mono text-xs">{u.shift_id}</span> — {u.reason}</li>)}
          </ul>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {preview.days.map((d, i) => (
          <button key={i} onClick={() => setActiveDay(i)} data-testid={`run-preview-tab-${i}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${activeDay === i ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"}`}>
            {dayName((d.config || {}).date)} · {d.legs.length}
          </button>
        ))}
      </div>
      {preview.days[activeDay] && (
        <div className={`grid grid-cols-1 ${showMaps ? "lg:grid-cols-2" : ""} gap-4 items-start`}>
          <DayTimetable day={preview.days[activeDay]} index={activeDay} />
          {showMaps && <div className="bg-white border border-slate-200 rounded-xl p-4 lg:sticky lg:top-6"><RouteMapLeaflet legs={preview.days[activeDay].legs} dayScan={scan?.days?.[activeDay]} height={620} testid="run-preview-map" /></div>}
        </div>
      )}
    </div>
  );
}
